import { useEffect, useState, useRef } from 'react';
import isEqual from 'lodash/isEqual';

interface UseFormChangesOptions<T> {
  initialValues: T;
  currentValues: T;
}

/**
 * Hook to track form changes by comparing current values with initial values
 * @param options Object containing initial and current form values
 * @returns Object containing hasChanges state and hasChanges ref and reset function
 */
export const useFormChanges = <T>({
  initialValues,
  currentValues,
}: UseFormChangesOptions<T>) => {
  const initialValuesRef = useRef<T>(initialValues);
  const hasChangesRef = useRef(false); // Use a ref to track changes synchronously
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    initialValuesRef.current = initialValues;
  }, [initialValues]);

  useEffect(() => {
    const hasUnsavedChanges = !isEqual(initialValuesRef.current, currentValues);
    hasChangesRef.current = hasUnsavedChanges;
    setHasChanges(hasUnsavedChanges);
  }, [currentValues]);

  const resetInitialValues = (callback?: () => void) => {
    initialValuesRef.current = currentValues;
    hasChangesRef.current = false;
    setHasChanges(false);
    callback?.();
  };

  return {
    hasChanges,
    hasChangesRef,
    resetInitialValues,
  };
};
